import React from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import { CxIdsDiff, FieldDiff, VariantsDiff } from '../utils/versionFieldDiff';
import VersionDiffTable from 'src/features/string-group-detail/components/VersionDiffTable';
import {
  DiffResult,
  NO_VALUE,
} from 'src/features/string-group-detail/utils/versionDiff';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface StringVersionDiffModalProps {
  visible: boolean;
  stringId: string;
  /** The version whose change is shown — the Current column. */
  version: number;
  /** Its predecessor in the version list — the Previous column. */
  previousVersion: number;
  sourceText: FieldDiff;
  contextNote: FieldDiff;
  /**
   * Inline markup / message format changes. Optional so a caller that does not
   * diff them (older tests) still type-checks; the panel always supplies them.
   */
  inlineMarkup?: FieldDiff;
  messageFormat?: FieldDiff;
  /** CX variant override changes, rendered one row per differing property. */
  variants?: VariantsDiff;
  /** Group-level CX ID membership change between the two versions. */
  cxIds: CxIdsDiff;
  onDismiss: () => void;
}

/**
 * Renders a version's CX ID membership as a sorted, comma-joined list, or the
 * shared placeholder when the version targeted none. Sorted so the two columns
 * line up field-for-field and the row reads deterministically regardless of the
 * backend's ordering.
 *
 * Deliberately NOT `joinList`: that is for lists inside a localized sentence and
 * yields a conjunction ("a, b, and c"). These are CX IDs — opaque ASCII
 * identifiers in a diff cell, not prose — and `Intl.ListFormat`'s separator-only
 * `unit` type is no help either, since it renders zh-CN as "abc".
 */
function formatCxIds(cxIds: string[]): string {
  return cxIds.length > 0
    ? [...cxIds].sort((a, b) => a.localeCompare(b)).join(', ')
    : NO_VALUE;
}

/**
 * What one version changed in a single string, field by field.
 *
 * Presentational only: both sides of the comparison come from snapshots the
 * panel already holds, so opening this modal fetches nothing. It renders the
 * shared {@link VersionDiffTable} — the same table the group page's DiffModal
 * and RollbackModal use — so the two diff surfaces never drift in layout,
 * change-coloring, or long-text handling. The group's DiffModal is not reused
 * wholesale because it diffs a whole group's string set and fetches two group
 * versions to do it; here the String ID / Change columns are dropped
 * (showStringColumns=false) since the id is already in the modal header.
 */
const StringVersionDiffModal: React.FC<StringVersionDiffModalProps> = ({
  visible,
  stringId,
  version,
  previousVersion,
  sourceText,
  contextNote,
  inlineMarkup,
  messageFormat,
  variants,
  cxIds,
  onDismiss,
}) => {
  const { t } = useTranslation();
  // One DiffResult for this single string, carrying only the fields that
  // actually changed — matching the group diff, which lists a changed string's
  // differing fields and nothing else. Empty values render as the shared
  // placeholder so a dropped field reads as "—". CX IDs are group-level
  // metadata, but a CX-ID-only version bumps the group version without touching
  // the text, so they are listed as their own row here rather than dropped.
  // Field order matches the Edit string modal and the summary: source text,
  // context note, inline markup, message format, then variants, then CX IDs.
  const scalarFields = [
    { label: t('string-field-source-text'), diff: sourceText },
    { label: t('string-field-context-note'), diff: contextNote },
    { label: t('string-field-inline-markup'), diff: inlineMarkup },
    { label: t('string-field-message-format'), diff: messageFormat },
  ]
    .filter(
      (entry): entry is { label: string; diff: FieldDiff } =>
        !!entry.diff && entry.diff.changed,
    )
    .map(({ label, diff }) => ({
      field: label,
      previous: diff.previous || NO_VALUE,
      current: diff.value || NO_VALUE,
    }));

  const fields = [
    ...scalarFields,
    // Variant rows already carry NO_VALUE placeholders (see diffVariants).
    ...(variants?.fields ?? []),
    ...(cxIds.changed
      ? [
          {
            field: t('string-meta-cx-ids'),
            previous: formatCxIds(cxIds.previous),
            current: formatCxIds(cxIds.value),
          },
        ]
      : []),
  ];

  const results: DiffResult[] =
    fields.length > 0 ? [{ stringId, changeType: 'changed', fields }] : [];

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      data-testid="string-diff-modal"
      closeAriaLabel={t('string-version-diff-close')}
      header={t('string-version-diff-header', { stringId })}
    >
      <VersionDiffTable
        results={results}
        previousHeader={t('sgd-diff-column-previous', {
          version: previousVersion,
        })}
        currentHeader={t('sgd-diff-column-current', { version })}
        showStringColumns={false}
      />
    </Modal>
  );
};

export default StringVersionDiffModal;
